import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaEdit } from "react-icons/fa";
import { HiOutlineDocumentText, HiOutlineAcademicCap, HiOutlineArrowRight, HiOutlineArrowLeft, HiOutlineCheckCircle } from "react-icons/hi2";

import api from "@/services/api";
import Loader from "@/components/loader";

function CourseCard({ course, index, total, isCompleted }) {
  return (
    <div className="group flex items-stretch rounded-2xl border border-gray-200 bg-white overflow-hidden hover:border-primary-300 hover:shadow-lg cursor-pointer transition-all duration-300 hover:-translate-y-1">
      <div className="relative w-40 min-w-[10rem] flex-shrink-0">
        {course.training_image ? (
          <img
            src={course.training_image}
            alt={course.training_title}
            className="absolute inset-0 w-full h-full object-cover bg-gray-50 group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
            <div className="text-center">
              <HiOutlineAcademicCap className="w-12 h-12 text-slate-400 mx-auto mb-2" />
              <div className="text-xs text-slate-500 font-medium">Course {index + 1}</div>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center bg-white/95 backdrop-blur-sm text-blue-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-blue-100 shadow-sm">
            {course.training_category || "TECH"}
          </span>
        </div>
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
            <HiOutlineArrowRight className="w-4 h-4 text-gray-700" />
          </div>
        </div>
      </div>
      <div className="flex flex-col flex-1 p-6 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider">
            Course {index + 1} of {total}
          </span>
        </div>
        <h4 className="text-xl font-semibold mb-2 text-gray-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">{course.training_title}</h4>
        <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed flex-1">
          {course.training_description || "Explore this comprehensive training module designed to enhance your skills and knowledge."}
        </p>
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center text-xs text-gray-500">
            <HiOutlineAcademicCap className="w-4 h-4 mr-1.5 text-primary-500" />
            <span className="font-medium">Course</span>
          </div>
          <span className="text-sm text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            Open course →
          </span>
        </div>
      </div>
    </div>
  );
}

export default function View() {
  const { id } = useParams();
  const [careerPath, setCareerPath] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    getCareerPath();
  }, [id]);

  async function getCareerPath() {
    try {
      setIsLoading(true);
      const { data, ok } = await api.get(`/career_path/${id}`);
      if (!ok) throw new Error("Failed to fetch career path");
      setCareerPath(data);
    } catch (error) {
      toast.error("Failed to fetch career path details");
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) return <Loader />;

  if (!careerPath) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg border border-gray-200 p-12 max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HiOutlineDocumentText className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Career Path Not Found</h2>
          <p className="text-gray-600 mb-6">The career path you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate("/selecole/career-path")}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors duration-200">
            Back to Career Paths
          </button>
        </div>
      </div>
    );
  }

  const courses = (careerPath.trainings || []).slice().sort((a, b) => (a.training_position || 0) - (b.training_position || 0));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-6xl mx-auto p-6">
        {/* Back Button */}
        <button
          onClick={() => navigate("/selecole/career-path")}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors duration-200 group">
          <HiOutlineArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
          <span className="font-medium">Back to Career Paths</span>
        </button>

        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex justify-between items-start gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center">
                  <HiOutlineAcademicCap className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 mb-1">Career Path</span>
                </div>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">{careerPath.name}</h1>
              {careerPath.description && <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">{careerPath.description}</p>}

              {/* Tags */}
              {careerPath.tags && careerPath.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {careerPath.tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {user && ["admin", "super_admin"].includes(user.role) && (
              <button
                onClick={() => navigate(`/selecole/career-path/${id}/edit`)}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 flex-shrink-0">
                <FaEdit className="w-4 h-4" />
                Edit Path
              </button>
            )}
          </div>

          {/* ── Course Progress Stepper ── */}
          {courses.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-700">{courses.length} Courses</span>
              </div>
              <div className="flex items-center gap-0">
                {courses.map((course, i) => (
                  <React.Fragment key={course.training_id}>
                    {/* Step dot + label */}
                    <button
                      onClick={() => navigate(`/selecole/course/${course.training_id}?pathId=${id}`)}
                      className="flex flex-col items-center group/step flex-shrink-0 relative"
                      title={course.training_title}>
                      <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold border-2 border-primary-200 group-hover/step:bg-primary-600 group-hover/step:text-white group-hover/step:border-primary-600 transition-all duration-200 shadow-sm">
                        {i + 1}
                      </div>
                      <span className="mt-2 text-xs font-medium text-gray-600 group-hover/step:text-primary-700 transition-colors max-w-[100px] text-center leading-tight line-clamp-2">
                        {course.training_title}
                      </span>
                    </button>
                    {/* Connector line */}
                    {i < courses.length - 1 && <div className="flex-1 h-0.5 bg-gray-200 mx-1 mt-[-20px] self-start relative top-5 min-w-[20px]" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Trainings Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Learning Journey</h2>
            <p className="text-gray-600">
              Work through each course in order. Each one builds on the previous, taking you from foundational skills to advanced expertise.
            </p>
          </div>

          {courses.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <HiOutlineDocumentText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Courses Available</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">This career path doesn't have any courses yet.</p>
              {user && ["admin", "super_admin"].includes(user.role) && (
                <button
                  onClick={() => navigate(`/selecole/career-path/${id}/edit`)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors duration-200">
                  Add Courses
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {courses.map((course, index) => (
                <div key={course.training_id} className="relative">
                  {/* Connector line between cards */}
                  {index > 0 && (
                    <div className="absolute -top-2 left-[15px] w-px h-4 bg-gradient-to-b from-primary-200 to-transparent" />
                  )}
                  <div className="flex items-center gap-4">
                    {/* Step number */}
                    <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-semibold border border-primary-200">
                      {index + 1}
                    </div>
                    {/* Card — now links with pathId */}
                    <div
                      className="flex-1 cursor-pointer transition-all duration-200"
                      onClick={() => navigate(`/selecole/course/${course.training_id}?pathId=${id}`)}>
                      <CourseCard course={course} index={index} total={courses.length} />
                    </div>
                  </div>

                  {/* "Next" connector between courses */}
                  {index < courses.length - 1 && (
                    <div className="flex items-center ml-[15px] my-1">
                      <div className="w-px h-6 bg-primary-200" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
